import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Gift, Loader2, Check } from "lucide-react";
import { api, euro, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const GutscheinKaufen = () => {
  const { lang, t: cms } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const g = cms.gutschein || {};
  const nav = useNavigate();
  const [cfg, setCfg] = useState({ presets: [25, 50, 100], min: 10, max: 1000 });
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [ppConfig, setPpConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", street: "", postcode: "", city: "", country: "Deutschland", recipient_name: "", recipient_email: "", message: "" });
  const [terms, setTerms] = useState(false);

  useEffect(() => {
    api.get("/vouchers/config").then(({ data }) => { setCfg(data); }).catch(() => {});
    api.get("/payments/paypal-config").then(({ data }) => setPpConfig(data)).catch(() => {});
  }, []);

  const effAmount = custom ? Math.round(Number(custom) * 100) / 100 : amount;
  const inputCls = "w-full bg-[#111] border border-white/15 text-white px-4 py-3 text-sm focus:border-[#ff0033] outline-none placeholder:text-neutral-600";

  const validate = () => {
    if (!effAmount || effAmount < cfg.min || effAmount > cfg.max) { toast.error(t(`Betrag zwischen ${cfg.min} € und ${cfg.max} € wählen`, `Choose an amount between €${cfg.min} and €${cfg.max}`)); return false; }
    if (!form.first_name || !form.last_name || !form.email) { toast.error(t("Bitte Name und E-Mail angeben", "Please enter name and email")); return false; }
    if (!terms) { toast.error(t("Bitte AGB bestätigen", "Please accept the terms")); return false; }
    return true;
  };
  const payload = () => ({ amount: effAmount, ...form, terms_accepted: terms, origin_url: window.location.origin });

  const payStripe = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/vouchers/checkout", payload());
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-28 pb-32 px-4" data-testid="gutschein-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <p className="font-mono-lab text-xs tracking-[0.25em] text-[#ff0033] uppercase mb-3">{g.kicker || t("Verschenken", "Gift")}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase text-white tracking-tight flex items-center justify-center gap-4"><Gift className="text-[#ff0033]" size={40} /> {g.title || t("Golf-Gutschein", "Golf Voucher")}</h1>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">{g.intro || t("Verschenke Golferlebnisse: Der Wertgutschein ist für Gruppenkurse und TrackMan einlösbar, 3 Jahre gültig und wird als PDF zum Ausdrucken versendet.", "Gift golf experiences: the value voucher works for group courses and TrackMan, is valid for 3 years and is sent as a printable PDF.")}</p>
        </div>

        <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8 space-y-6">
          <div>
            <p className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase mb-3">{t("Betrag wählen", "Choose amount")}</p>
            <div className="grid grid-cols-4 gap-2">
              {cfg.presets.map((p) => (
                <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`amount-${p}`} className={`py-4 font-heading text-xl border transition-colors ${!custom && amount === p ? "border-[#ff0033] text-white bg-[#ff0033]/10" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>{p} €</button>
              ))}
              <input data-testid="amount-custom" type="number" placeholder={t("Wunsch €", "Custom €")} value={custom} onChange={(e) => setCustom(e.target.value)} className={`py-4 text-center bg-[#111] border ${custom ? "border-[#ff0033] text-white" : "border-white/15 text-neutral-300"} outline-none`} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="gc-first" placeholder={t("Dein Vorname", "Your first name")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
            <input data-testid="gc-last" placeholder={t("Dein Nachname", "Your last name")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
            <input data-testid="gc-email" placeholder={t("Deine E-Mail", "Your email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="gc-phone" placeholder={t("Telefon (optional)", "Phone (optional)")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
          </div>

          <div className="border-t border-white/10 pt-5">
            <p className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase mb-3">{t("Für wen? (optional)", "For whom? (optional)")}</p>
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="gc-rec-name" placeholder={t("Name des Beschenkten", "Recipient name")} value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className={inputCls} />
              <input data-testid="gc-rec-email" placeholder={t("E-Mail des Beschenkten (optional)", "Recipient email (optional)")} value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className={inputCls} />
            </div>
            <textarea data-testid="gc-message" placeholder={t("Persönliche Grußnachricht (erscheint auf dem Gutschein)", "Personal message (appears on the voucher)")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className={`${inputCls} mt-3 resize-none`} />
            <p className="text-[11px] text-neutral-500 mt-2">{t("Gibst du eine E-Mail des Beschenkten an, senden wir den Gutschein zusätzlich direkt an ihn.", "If you provide the recipient's email, we'll also send the voucher directly to them.")}</p>
          </div>

          <label className="flex items-start gap-3 text-sm text-neutral-300">
            <input type="checkbox" data-testid="gc-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="accent-[#ff0033] mt-1 shrink-0" />
            <span>{t("Ich akzeptiere die AGB und Datenschutzbestimmungen.", "I accept the terms and privacy policy.")}</span>
          </label>

          <div className="border-t border-white/10 pt-5">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono-lab text-xs tracking-widest text-neutral-500 uppercase">{t("Gutscheinwert", "Voucher value")}</span>
              <span className="font-heading text-3xl text-white" data-testid="gc-total">{euro(effAmount || 0)}</span>
            </div>

            {ppConfig?.enabled && ppConfig?.client_id && (
              <div className="mb-3" data-testid="gc-paypal">
                <p className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase mb-2">{t("Mit PayPal bezahlen", "Pay with PayPal")}</p>
                <PayPalScriptProvider options={{ clientId: ppConfig.client_id, currency: "EUR", components: "buttons", intent: "capture" }}>
                  <PayPalButtons
                    fundingSource="paypal"
                    forceReRender={[effAmount]}
                    style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 48 }}
                    onClick={(d, actions) => (validate() ? actions.resolve() : actions.reject())}
                    createOrder={async () => {
                      const { data } = await api.post("/vouchers/paypal/checkout", payload());
                      return data.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const { data: res } = await api.post(`/vouchers/paypal/capture/${data.orderID}`);
                        if (res.payment_status === "paid") nav(`/gutschein/success?order=${data.orderID}`);
                        else toast.error(t("Zahlung nicht abgeschlossen.", "Payment not completed."));
                      } catch (e) { toast.error(apiError(e)); }
                    }}
                    onCancel={() => toast.message(t("PayPal abgebrochen.", "PayPal cancelled."))}
                    onError={() => toast.error(t("PayPal-Fehler. Bitte mit Karte zahlen.", "PayPal error. Please pay by card."))}
                  />
                </PayPalScriptProvider>
                <div className="flex items-center gap-3 my-4"><div className="h-px bg-white/10 flex-1" /><span className="text-neutral-500 text-[11px] font-mono-lab uppercase">{t("oder", "or")}</span><div className="h-px bg-white/10 flex-1" /></div>
              </div>
            )}

            <button onClick={payStripe} disabled={loading} data-testid="gc-pay-card" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 hover:bg-[#cc0029] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="animate-spin" size={18} /> : t("Gutschein mit Karte kaufen", "Buy voucher by card")}
            </button>
            <p className="text-[10px] text-neutral-600 mt-2">{t("Sichere Zahlung über Stripe. Der Gutschein wird dir direkt per E-Mail als PDF zugesendet.", "Secure payment via Stripe. The voucher PDF is emailed to you immediately.")}</p>
          </div>
        </div>

        <div className="mt-6 flex items-start gap-3 text-sm text-neutral-400">
          <Check size={16} className="text-[#ff0033] mt-0.5 shrink-0" />
          <p>{g.redeemNote || t("Einlösbar für Gruppenkurse und TrackMan-Buchungen. Nicht für Einzeltraining/1:1. Teil-Einlösung möglich – Restguthaben bleibt erhalten.", "Redeemable for group courses and TrackMan. Not for 1:1 training. Partial redemption possible – remaining balance stays.")}</p>
        </div>
      </div>
    </div>
  );
};

export default GutscheinKaufen;
